import { useMemo } from "react";

/**
 * Mirrors the form mode logic at the top of ContractReviewFrm.vb (Pass 1 §1.6).
 * Returns a set of boolean flags every form sub-component reads from. Centralising
 * this prevents three different places re-deriving "can I edit responses?".
 *
 * @param {object|null} cr        — the loaded CrDetailDto, or null for new CRs
 * @param {string|number} myEmpId — current login empId
 * @param {boolean} isCrAdmin     — "Contract Review - Admin" (module cr.admin; IT Admin passes)
 * @param {Array} employees       — employee options ({ empId, supervisorId }) used to recognise
 *                                  the author's / a responsible's supervisor
 */
export default function useContractReviewMode(cr, myEmpId, isCrAdmin, employees) {
    return useMemo(() => {
        const me = Number(myEmpId);

        if (!cr) {
            // Brand-new CR being created.
            return {
                isNew: true,
                isSaved: false,
                isInProgress: false,
                isReady: false,
                isArchived: false,
                isAuthor: true,
                isAuthorSupv: false,
                amResponsible: false,
                isCrAdmin,

                // capability flags
                canEditHeader: true,
                canEditResponses: true,
                canSetResponsibilities: true,
                canSubmit: true,
                canDeleteCr: false,           // can't delete a brand-new CR
                canCancel: false,
                canArchive: false,
                canWonLost: false,
                canAddNotes: true,            // the author; the Notes tab asks to save first
                canUploadAttachments: true,   // the author; the Attachments tab asks to save first
                canAttachAfterArchive: false,
                showSetResponsibilitiesTab: true,
                showWonLostBar: false,
            };
        }

        // Go-live parity (2026-09-24): supervisor look-up for the C5 notes/attachments gate.
        const supervisorOf = (empId) => {
            const e = (employees || []).find((x) => Number(x.empId) === Number(empId));
            const s = e && e.supervisorId != null ? String(e.supervisorId).trim() : "";
            return s ? Number(s) : NaN;
        };

        const isAuthor = Number(cr.createdAuthor) === me;
        const isAuthorSupv = supervisorOf(cr.createdAuthor) === me;
        const isArchived = !!cr.archived;
        const isSaved = !!cr.saved;
        const authorSubmitted = !!cr.authorSubmit;
        const isReady = !!cr.isReady;
        const isInProgress = authorSubmitted && !isSaved && !isArchived && !isReady;

        const amResponsible =
            cr.keyResponses?.some((r) => Number(r.responsible) === me) ||
            cr.salesResponses?.some((r) => Number(r.responsible) === me) ||
            cr.engineeringResponses?.some((r) => Number(r.responsible) === me) ||
            cr.materialsResponses?.some((r) => Number(r.responsible) === me) ||
            cr.qualityResponses?.some((r) => Number(r.responsible) === me);

        const quoteOnly = (cr.quoteNumber ?? 0) > 0 && (cr.salesOrderNum ?? 0) === 0;

        // Go-live parity (2026-09-24): N5 — legacy lets the author OR the author's supervisor edit and
        // submit a saved draft (ContractReviewFrm.vb:249, 497-499); the server allows both.
        const canEditHeader = (isAuthor || isAuthorSupv) && (isSaved || !authorSubmitted);
        const canSetResponsibilities = !authorSubmitted && (isAuthor || isAuthorSupv);
        // Go-live parity (2026-09-24): S11 — Submit only for the author / author's supervisor on a draft.
        const canSubmit = isSaved && !authorSubmitted && !isArchived && (isAuthor || isAuthorSupv);
        const canCancel = isAuthor && isInProgress;
        // Go-live parity (2026-09-24): S3 — archive / Won-Lost: the author, the author's supervisor or
        // a Contract Review admin (same rule as the server).
        const canSettle = isAuthor || isAuthorSupv || isCrAdmin;
        const canArchive = canSettle && isReady;
        const canWonLost = canSettle && isReady && (cr.quoteNumber ?? 0) > 0;
        // Go-live parity (2026-09-24): C4 — same rule as the server: a Contract Review admin, or
        // the author on their own never-submitted draft.
        const isNeverSubmittedDraft = isSaved && !authorSubmitted && !isArchived;
        const canDeleteCr = !!cr.id && (isCrAdmin || (isAuthor && isNeverSubmittedDraft));
        // Go-live parity (2026-09-24): C5 — same rule as the server: notes / attachments need the
        // author, the author's supervisor, a responsible, a responsible's supervisor, or a
        // Contract Review admin. An archived CR takes no notes, and attachments only from admins.
        const isResponsibleSupv = [
            ...(cr.keyResponses || []),
            ...(cr.salesResponses || []),
            ...(cr.engineeringResponses || []),
            ...(cr.materialsResponses || []),
            ...(cr.qualityResponses || []),
        ].some((r) => r.responsible != null && supervisorOf(r.responsible) === me);
        const isParticipant = isAuthor || isAuthorSupv || !!amResponsible || isResponsibleSupv || isCrAdmin;
        // Once author submitted, only the responsible employee (or author) can edit their rows.
        // Server enforces; UI shows the field as editable to anyone with a row.
        // Go-live parity (2026-09-24): S4 — a responsible's supervisor may answer for their report
        // (legacy ContractReviewFrm.vb:902, 1000, 4190-4194; the server allows it). RequirementGrid
        // still limits each row to its responsible or that person's supervisor.
        const canEditResponses = !isArchived && (isAuthor || !!amResponsible || isResponsibleSupv);
        const canAddNotes = !isArchived && isParticipant;
        const canUploadAttachments = isArchived ? isCrAdmin : isParticipant;
        const canAttachAfterArchive = isArchived && isCrAdmin;

        return {
            isNew: false,
            isSaved,
            isInProgress,
            isReady,
            isArchived,
            isAuthor,
            isAuthorSupv,
            amResponsible: !!amResponsible,
            isCrAdmin,
            quoteOnly,

            canEditHeader,
            canEditResponses,
            canSetResponsibilities,
            canSubmit,
            canDeleteCr,
            canCancel,
            canArchive,
            canWonLost,
            canAddNotes,
            canUploadAttachments,
            canAttachAfterArchive,
            showSetResponsibilitiesTab: !authorSubmitted,
            showWonLostBar: isReady && (cr.quoteNumber ?? 0) > 0,
        };
    }, [cr, myEmpId, isCrAdmin, employees]);
}